import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCase, uploadAttachments } from '../services/caseApi';
import { ArrowLeft, Send, Paperclip, Info, CircleHelp, X } from 'lucide-react';
import InfoModal from '../components/InfoModal';

const inputBaseStyle = {
  width: '100%',
  padding: '0 14px',
  height: 44,
  borderRadius: 10,
  border: '1px solid #E2E8F0',
  fontSize: 14,
  fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
  color: '#0F172A',
  background: '#FFFFFF',
  outline: 'none',
  transition: 'border-color 0.15s ease',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#0F172A',
  marginBottom: 8
};

function CreateTicket() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    reporting_mode: 'normal',
    attachments: [],
    ccRecipients: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [showReportingModeHelp, setShowReportingModeHelp] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [showCcDropdown, setShowCcDropdown] = useState(false);
  const [ccSearch, setCcSearch] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [duplicateTicketInfo, setDuplicateTicketInfo] = useState(null);
  const ccDropdownRef = useRef(null);
  const submittingRef = useRef(false);

  // Auto-dismiss popup after 3 seconds
  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
        setPopupMessage('');
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  const reportingModePriority = {
    normal: 'Medium',
    confidential: 'High',
    anonymous: 'High',
    sensitive: 'High'
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const res = await fetch('/api/v2/categories', { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setCategories(data.data || []);
        } else {
          const text = await res.text().catch(() => '');
          console.error('Failed to load categories:', res.status, text);
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category) {
        setSubcategories([]);
        return;
      }
      const category = categories.find(c => c.name === formData.category);
      if (!category) return;
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const res = await fetch(`/api/v2/categories/${category.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const seen = new Set();
          const unique = (data.data?.subcategories || []).filter(s => {
            if (s.status === 'inactive') return false;
            if (seen.has(s.name)) return false;
            seen.add(s.name);
            return true;
          });
          setSubcategories(unique);
        } else {
          const text = await res.text().catch(() => '');
          console.error('Failed to load subcategories:', res.status, text);
        }
      } catch (err) {
        console.error('Failed to load subcategories:', err);
      }
    };
    fetchSubcategories();
  }, [formData.category, categories]);

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const res = await fetch('/api/v2/cases/users/active', { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setActiveUsers(data.data || []);
        } else {
          const text = await res.text().catch(() => '');
          console.error('Failed to load active users:', res.status, text);
        }
      } catch (err) {
        console.error('Failed to load active users:', err);
      }
    };
    fetchActiveUsers();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ccDropdownRef.current && !ccDropdownRef.current.contains(e.target)) {
        setShowCcDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCcRecipientToggle = (userId) => {
    setFormData(prev => {
      const current = prev.ccRecipients || [];
      if (current.includes(userId)) {
        return { ...prev, ccRecipients: current.filter(id => id !== userId) };
      }
      return { ...prev, ccRecipients: [...current, userId] };
    });
  };

  const removeCcRecipient = (userId) => {
    setFormData(prev => ({ ...prev, ccRecipients: (prev.ccRecipients || []).filter(id => id !== userId) }));
  };

  const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'doc', 'docx', 'xls', 'xlsx'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const isValidFileType = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    return ALLOWED_EXTENSIONS.includes(ext);
  };

  const isValidFileName = (file) => {
    const fileName = file.name;
    
    // Check for double extensions (e.g., file.pdf.pdf)
    const parts = fileName.split('.');
    if (parts.length > 2) {
      return false;
    }
    
    // Check for obviously invalid characters (like @, #, $, %, etc.)
    // Allow alphanumeric, spaces, hyphens, underscores, dots, and parentheses
    const invalidChars = /[<>:"|?*@#$%^&+={}\[\]\\]/;
    if (invalidChars.test(fileName)) {
      return false;
    }
    
    // Check for meaningful filename (not just UUIDs or random characters)
    const nameWithoutExt = parts[0];
    
    // Reject if it looks like a UUID pattern
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(nameWithoutExt)) {
      return false;
    }
    
    // Reject if it's just numbers and hyphens (no letters)
    const hasLetters = /[a-zA-Z]/.test(nameWithoutExt);
    if (!hasLetters) {
      return false;
    }
    
    // Reject if it's too short (less than 3 characters)
    if (nameWithoutExt.length < 3) {
      return false;
    }
    
    // Check if the extension matches the actual file type
    const ext = parts[parts.length - 1].toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return false;
    }
    
    return true;
  };

  const validateFiles = (files) => {
    const validFiles = [];
    const errors = [];
    files.forEach(file => {
      if (!isValidFileType(file)) {
        const ext = file.name.split('.').pop().toLowerCase();
        errors.push(`The .${ext} file type is not supported`);
      } else if (!isValidFileName(file)) {
        errors.push(`${file.name}: Invalid file name format. Only single extensions and standard characters are allowed.`);
      } else if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        errors.push(`${file.name}: File is too large (${sizeMB}MB). Maximum allowed size is 10MB.`);
      } else {
        validFiles.push(file);
      }
    });
    return { validFiles, errors };
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const { validFiles, errors } = validateFiles(files);
    if (errors.length > 0) {
      setPopupMessage(errors.join('\n'));
      setShowPopup(true);
    }
    if (validFiles.length > 0) {
      setFormData(prev => {
        const combined = [...prev.attachments, ...validFiles];
        if (combined.length > 5) {
          setPopupMessage('You can attach up to 5 files only.');
          setShowPopup(true);
          return { ...prev, attachments: combined.slice(0, 5) };
        }
        return { ...prev, attachments: combined };
      });
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const { validFiles, errors } = validateFiles(files);
    if (errors.length > 0) {
      setPopupMessage(errors.join('\n'));
      setShowPopup(true);
    }
    if (validFiles.length > 0) {
      setFormData(prev => {
        const combined = [...prev.attachments, ...validFiles];
        if (combined.length > 5) {
          setPopupMessage('You can attach up to 5 files only.');
          setShowPopup(true);
          return { ...prev, attachments: combined.slice(0, 5) };
        }
        return { ...prev, attachments: combined };
      });
    }
    e.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const handlePreviewAttachment = (file) => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setPreviewFile(file);
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewFile(null);
  };

  const isPreviewable = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    return ['png', 'jpg', 'jpeg', 'pdf'].includes(ext);
  };

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const MAX_DESCRIPTION_CHARS = 500;
  const MAX_SUBJECT_CHARS = 120;

  const countChars = (text) => {
    return text ? text.length : 0;
  };

  const isFormValid = () => {
    if (!formData.category.trim()) return false;
    if (!formData.subcategory.trim()) return false;
    if (!formData.reporting_mode.trim()) return false;
    if (!formData.title.trim()) return false;
    if (formData.title.trim().length > MAX_SUBJECT_CHARS) return false;
    if (!formData.description.trim()) return false;
    if (countChars(formData.description) > MAX_DESCRIPTION_CHARS) return false;
    if (formData.attachments.length > 5) return false;
    const oversized = formData.attachments.find(f => f.size > 10 * 1024 * 1024);
    if (oversized) return false;
    return true;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.category.trim()) newErrors.category = 'Please select a category.';
    if (!formData.subcategory.trim()) newErrors.subcategory = 'Please select a subcategory.';
    if (!formData.reporting_mode.trim()) newErrors.reporting_mode = 'Please select a reporting mode.';
    if (!formData.title.trim()) {
      newErrors.title = 'Please enter a subject for your ticket.';
    } else if (formData.title.trim().length > MAX_SUBJECT_CHARS) {
      newErrors.title = `Subject is too long (max ${MAX_SUBJECT_CHARS} characters).`;
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Please describe your issue.';
    } else if (countChars(formData.description) > MAX_DESCRIPTION_CHARS) {
      newErrors.description = `Description is too long (max ${MAX_DESCRIPTION_CHARS} characters).`;
    }
    if (formData.attachments.length > 5) {
      newErrors.attachments = 'You can attach up to 5 files only.';
    }
    const oversized = formData.attachments.find(f => f.size > 10 * 1024 * 1024);
    if (oversized) {
      newErrors.attachments = `File "${oversized.name}" exceeds 10MB limit.`;
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submittingRef.current) return;
    setError('');

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        subcategory: formData.subcategory,
        reporting_mode: formData.reporting_mode,
        ccRecipients: formData.ccRecipients
      };
      const result = await createCase(payload);
      if (result.success) {
        // Upload attachments if any
        if (formData.attachments.length > 0) {
          try {
            await uploadAttachments(result.case.id, formData.attachments);
          } catch (uploadErr) {
            // Show error to user
            const errorData = uploadErr.response?.data;
            const errorMsg = errorData?.message || errorData?.error || uploadErr.message || 'Upload failed';
            const errorDetails = errorData?.details || errorData?.code || '';
            const fullError = errorDetails ? `${errorMsg} (${errorDetails})` : errorMsg;
            setPopupMessage(`Upload failed: ${fullError}`);
            setShowPopup(true);
            // Continue even if upload fails
          }
        }
        // Show success message and redirect to ticket list
        setPopupMessage('Ticket created successfully!');
        setShowPopup(true);
        setTimeout(() => navigate('/hcms/tickets', { replace: true }), 2000);
      } else if (result.duplicate && result.existingTicketId) {
        const duplicateMessage = result.message || 'A similar ticket already exists. Please check the existing ticket or modify your subject.';
        setPopupMessage(duplicateMessage);
        setShowPopup(true);
        // Stay on create ticket page instead of redirecting to existing ticket
      } else {
        const msg = result.message || 'Failed to create ticket';
        setPopupMessage(msg.includes('organization location') ? 'No HR assigned at your location' : 'Failed to create ticket');
        setShowPopup(true);
      }
    } catch (err) {
      console.error('[CreateTicket] Failed to create ticket:', err);
      const errData = err.response?.data;
      if (errData?.duplicate && errData?.existingTicketId) {
        const duplicateMessage = errData.message || 'A similar ticket already exists. Please check the existing ticket or modify your subject.';
        setPopupMessage(duplicateMessage);
        setShowPopup(true);
        // Stay on create ticket page instead of redirecting to existing ticket
      } else if (errData?.field) {
        setErrors(prev => ({ ...prev, [errData.field]: errData.message || 'Invalid input.' }));
      } else {
        const errorMsg = errData?.message || 'Network error. Please try again.';
        setPopupMessage(errorMsg);
        setShowPopup(true);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" }}>
      {/* Back Button */}
      <div>
        <button
          onClick={() => navigate('/hcms/tickets')}
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            color: '#64748B',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'border-color 0.15s ease, background 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#CBD5E1';
            e.currentTarget.style.background = '#F8FAFC';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#E2E8F0';
            e.currentTarget.style.background = '#FFFFFF';
          }}
        >
          <ArrowLeft size={18} strokeWidth={2} />
        </button>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1.4fr', gap: 20, alignItems: 'start' }}>
        {/* Left: Ticket Details Card */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', overflow: 'hidden' }}>
          {/* Card Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>Ticket details</h2>
            <p style={{ fontSize: 13, fontWeight: 400, color: '#64748B', margin: 0 }}>All fields marked with <span style={{ color: '#EF4444' }}>*</span> are required</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Category & Subcategory */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>
                  Category <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, category: e.target.value, subcategory: '', reporting_mode: 'normal' }));
                    if (errors.category) setErrors(prev => ({ ...prev, category: '', subcategory: '' }));
                  }}
                  style={{ ...inputBaseStyle, cursor: 'pointer', ...(errors.category ? { borderColor: '#EF4444' } : {}) }}
                >
                  <option value="">Select category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                {errors.category && <div style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.category}</div>}
              </div>
              <div>
                <label style={labelStyle}>
                  Subcategory <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleChange}
                  style={{ ...inputBaseStyle, cursor: 'pointer', ...(errors.subcategory ? { borderColor: '#EF4444' } : {}) }}
                  disabled={!formData.category}
                >
                  <option value="">{formData.category ? 'Choose subcategory' : 'Choose category first'}</option>
                  {subcategories.map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
                {errors.subcategory && <div style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.subcategory}</div>}
              </div>
            </div>

            {/* Reporting Mode */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <label style={labelStyle}>
                  Reporting mode <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowReportingModeHelp(true)}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: 'none',
                    background: '#E2E8F0',
                    color: '#475569',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                  aria-label="Learn about reporting modes"
                  title="Learn about reporting modes"
                >
                  <CircleHelp size={14} />
                </button>
              </div>
              <select
                name="reporting_mode"
                value={formData.reporting_mode}
                onChange={handleChange}
                style={{ ...inputBaseStyle, cursor: 'pointer', ...(errors.reporting_mode ? { borderColor: '#EF4444' } : {}) }}
                disabled={!formData.category || !formData.subcategory}
              >
                <option value="normal">Normal</option>
                <option value="confidential">Confidential</option>
                <option value="anonymous">Anonymous</option>
                <option value="sensitive">Sensitive</option>
              </select>
              {errors.reporting_mode && <div style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.reporting_mode}</div>}
              <p style={{ fontSize: 12, color: '#64748B', margin: '6px 0 0' }}>
                <Info size={12} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                Choose the mode that best describes how sensitive or restricted this matter is.
              </p>
            </div>

            {/* Priority (read-only, auto-assigned) */}
            <div>
              <label style={labelStyle}>
                Priority
              </label>
              <div
                style={{
                  ...inputBaseStyle,
                  display: 'flex',
                  alignItems: 'center',
                  background: '#F8FAFC',
                  color: '#475569',
                  cursor: 'not-allowed',
                  fontWeight: 500
                }}
              >
                {reportingModePriority[formData.reporting_mode] || 'Medium'}
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94A3B8', fontWeight: 400 }}>Auto-assigned</span>
              </div>
              <p style={{ fontSize: 12, color: '#64748B', margin: '6px 0 0' }}>
                Priority is automatically set based on the reporting mode.
              </p>
            </div>

            {/* CC Recipients (only for confidential, sensitive, anonymous) */}
            {['confidential', 'sensitive', 'anonymous'].includes(formData.reporting_mode) && (
              <div>
                <label style={labelStyle}>
                  CC Recipients
                </label>
                <div ref={ccDropdownRef} style={{ position: 'relative' }}>
                  {/* Selected tags + trigger */}
                  <div
                    onClick={() => setShowCcDropdown(!showCcDropdown)}
                    style={{
                      ...inputBaseStyle,
                      height: 'auto',
                      minHeight: 44,
                      padding: '8px 10px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer'
                    }}
                  >
                    {formData.ccRecipients.length === 0 ? (
                      <span style={{ color: '#94A3B8', fontSize: 14 }}>Select CC recipients</span>
                    ) : (
                      formData.ccRecipients.map(id => {
                        const ccUser = activeUsers.find(u => u.id === id);
                        return (
                          <span
                            key={id}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '4px 8px',
                              background: '#F1F5F9',
                              borderRadius: 6,
                              fontSize: 12,
                              color: '#0F172A'
                            }}
                          >
                            {ccUser?.name || `User #${id}`}
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); removeCcRecipient(id); }}
                              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: '#64748B', display: 'flex' }}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>

                  {/* Dropdown with search and checkboxes */}
                  {showCcDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10, boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 300, overflow: 'hidden' }}>
                      <div style={{ padding: 8, borderBottom: '1px solid #F1F5F9' }}>
                        <input
                          type="text"
                          value={ccSearch}
                          onChange={e => setCcSearch(e.target.value)}
                          placeholder="Search users..."
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {(() => {
                          const filteredUsers = activeUsers.filter(u => u.id !== user?.id && (!ccSearch || (u.name || '').toLowerCase().includes(ccSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(ccSearch.toLowerCase())));
                          if (filteredUsers.length === 0) {
                            return <div style={{ padding: 12, color: '#94A3B8', fontSize: 13, textAlign: 'center' }}>{activeUsers.length === 0 ? 'Loading users...' : 'No users found'}</div>;
                          }
                          return filteredUsers.map(ccUser => {
                            const selected = formData.ccRecipients.includes(ccUser.id);
                            return (
                              <div
                                key={ccUser.id}
                                onClick={() => handleCcRecipientToggle(ccUser.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', background: selected ? '#F8FAFC' : '#FFFFFF', borderBottom: '1px solid #F8FAFC' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => { e.stopPropagation(); handleCcRecipientToggle(ccUser.id); }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ cursor: 'pointer' }}
                                />
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{ccUser.name || '—'}</div>
                                  <div style={{ fontSize: 12, color: '#64748B' }}>{ccUser.email || '—'}</div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                <p style={{ fontSize: 12, color: '#64748B', margin: '6px 0 0' }}>
                  <Info size={12} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                  Optionally add users to be notified and grant them view access.
                </p>
              </div>
            )}

            {/* Subject */}
            <div>
              <label style={labelStyle}>
                Subject <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Brief summary of the issue"
                style={{ ...inputBaseStyle, ...(errors.title ? { borderColor: '#EF4444' } : {}) }}
              />
              <div style={{ fontSize: 12, color: errors.title ? '#EF4444' : (formData.title.length > MAX_SUBJECT_CHARS ? '#EF4444' : '#64748B'), marginTop: 4, textAlign: 'right' }}>
                {errors.title || `${formData.title.length}/${MAX_SUBJECT_CHARS} characters`}
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>
                Description <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the issue, when it started, and any steps already taken..."
                  rows={6}
                  style={{
                    ...inputBaseStyle,
                    height: 140,
                    padding: '14px',
                    resize: 'none',
                    ...(errors.description ? { borderColor: '#EF4444' } : {})
                  }}
                />
                {!errors.description && (
                  <div style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 12,
                    fontSize: 11,
                    color: countChars(formData.description) > MAX_DESCRIPTION_CHARS ? '#EF4444' : '#94A3B8',
                    background: 'rgba(255,255,255,0.9)',
                    padding: '2px 6px',
                    borderRadius: 4,
                    pointerEvents: 'none'
                  }}>
                    {countChars(formData.description)}/{MAX_DESCRIPTION_CHARS} chars
                  </div>
                )}
              </div>
              {errors.description && (
                <div style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.description}</div>
              )}
            </div>

            {/* Attachments */}
            <div>
              <label style={{ ...labelStyle, marginBottom: 8 }}>Attachments</label>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                style={{
                  height: 120,
                  border: '1px dashed #E2E8F0',
                  background: '#FAFBFC',
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s ease, background 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#CBD5E1';
                  e.currentTarget.style.background = '#F8FAFC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E2E8F0';
                  e.currentTarget.style.background = '#FAFBFC';
                }}
              >
                <input
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="attachment-input"
                />
                <label htmlFor="attachment-input" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, margin: 0 }}>
                  <Paperclip size={18} color="#64748B" strokeWidth={2} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>Drag files here or choose file</span>
                  <span style={{ fontSize: 12, color: '#64748B' }}>PNG, JPG, PDF, Word, Excel up to 10MB each</span>
                </label>
              </div>
              {errors.attachments && (
                <div style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.attachments}</div>
              )}
              {formData.attachments.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {formData.attachments.map((file, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                      <Paperclip size={12} color="#64748B" />
                      <span
                        onClick={() => handlePreviewAttachment(file)}
                        style={{ cursor: 'pointer', color: '#2563EB', textDecoration: 'underline', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {file.name}
                      </span>
                      <button
                        onClick={() => handleRemoveAttachment(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 2, display: 'flex', alignItems: 'center' }}
                        title="Remove file"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>

          {/* Bottom Action Bar */}
          <div style={{ borderTop: '1px solid #E2E8F0', padding: '18px 24px', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              type="button"
              onClick={() => navigate('/hcms/tickets')}
              style={{
                height: 40,
                padding: '0 18px',
                borderRadius: 10,
                background: '#FFFFFF',
                color: '#0F172A',
                border: '1px solid #E2E8F0',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                cursor: 'pointer',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid()}
              style={{
                height: 40,
                padding: '0 18px',
                borderRadius: 10,
                background: (isSubmitting || !isFormValid()) ? '#CBD5E1' : '#0F172A',
                color: '#FFFFFF',
                border: 'none',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                cursor: (isSubmitting || !isFormValid()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'background 0.15s ease',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => !isSubmitting && isFormValid() && (e.currentTarget.style.background = '#020617')}
              onMouseLeave={(e) => { if (isFormValid()) e.currentTarget.style.background = '#0F172A'; }}
            >
              <Send size={16} strokeWidth={2} />
              {isSubmitting ? 'Submitting...' : 'Submit ticket'}
            </button>
          </div>
        </div>

        {/* Right Sidebar Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Before You Submit Card */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Info size={18} color="#0F172A" strokeWidth={2} />
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', margin: 0 }}>Before you submit</h3>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                'Choose the most specific subcategory for faster routing.',
                'Attach relevant docs (payslips, screenshots) to avoid back-and-forth.',
                'Average first response: under 4 working hours.'
              ].map((item, i) => (
                <li key={i} style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Need To Talk To Someone Card */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', padding: 18 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>Need to talk to someone?</h3>
            <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 14px' }}>Reach HR Helpdesk directly for urgent matters.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 2 }}>Email</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A' }}>hr-help@acme.co</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 2 }}>Phone</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A' }}>+1 (415) 555-0123</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Centered Popup */}
      {showPopup && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: 12,
          padding: '20px 24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          zIndex: 1000,
          maxWidth: 500,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10
        }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: popupMessage.includes('successfully') ? '#D1FAE5' : '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {popupMessage.includes('successfully') ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A', whiteSpace: 'pre-line' }}>
            {popupMessage}
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={closePreview}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              width: '90%',
              maxWidth: 800,
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderBottom: '1px solid #E2E8F0'
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {previewFile.name}
              </span>
              <button onClick={closePreview} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4 }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: '#F8FAFC' }}>
              {isPreviewable(previewFile) ? (
                previewFile.type.startsWith('image/') ? (
                  <img src={previewUrl} alt={previewFile.name} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                ) : (
                  <iframe src={previewUrl} title={previewFile.name} style={{ width: '100%', height: '70vh', border: 'none' }} />
                )
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 40 }}>
                  <Paperclip size={48} color="#94A3B8" />
                  <div style={{ fontSize: 14, color: '#64748B', textAlign: 'center' }}>
                    Preview is not available for this file type.<br />You can download it to view.
                  </div>
                  <a
                    href={previewUrl}
                    download={previewFile.name}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 8,
                      background: '#2563EB',
                      color: '#FFFFFF',
                      fontSize: 13,
                      fontWeight: 600,
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <InfoModal
        isOpen={showReportingModeHelp}
        onClose={() => setShowReportingModeHelp(false)}
        title="Reporting mode guide"
        items={[
          {
            label: 'Normal',
            description: 'Use this for regular HRMS requests such as leave, attendance, payroll, employee information, reimbursement, etc.'
          },
          {
            label: 'Confidential',
            description: 'Use this when the matter contains sensitive information that should only be visible to authorized HR personnel.'
          },
          {
            label: 'Anonymous',
            description: 'Use this when you want to report an issue without revealing your identity to the investigation team.'
          },
          {
            label: 'Sensitive',
            description: 'Use this for cases requiring restricted visibility due to personal, legal or organizational sensitivity.'
          }
        ]}
        footer={
          <div style={{ fontSize: 13, color: '#64748B' }}>
            Pick the mode that best matches how private or restricted the matter should be.
          </div>
        }
      />
    </div>
  );
}

export default CreateTicket;
